import {
  Body2,
  Box,
  Button,
  ButtonColorVariant,
  ButtonSize,
  Color,
  Display,
  FlexDirection,
  Heading2,
} from '@gousto-internal/citrus-react'
import type {
  DashboardExperienceActionResponse,
  DashboardExperienceRafCampaignResponse,
  DashboardExperienceRafStatusResponse,
} from '@library/api-dashboard-experience'
import React from 'react'

import { RafActionCard } from './RafActionCard'
import { RafCampaignTiers } from './RafCampaignTiers'
import { RafStatusMetrics } from './RafStatusMetrics'

type RafCampaignActionProps = {
  campaign: DashboardExperienceRafCampaignResponse
  onActionClick?: (actionType: DashboardExperienceActionResponse['type']) => void
}

type RafStatusActionProps = {
  status: DashboardExperienceRafStatusResponse
  onActionClick?: (actionType: DashboardExperienceActionResponse['type']) => void
}

type RafButtonProps = {
  children: React.ReactNode
  onClick: () => void
}

function RafShareIcon() {
  return (
    <svg
      aria-hidden="true"
      data-testid="ios_share"
      focusable="false"
      height="18"
      viewBox="0 0 24 24"
      width="18"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zM20 10v11H4V10h5v2H6v7h12v-7h-3v-2h5z"
        fill="currentColor"
      />
    </svg>
  )
}

function RafButtonContent({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ alignItems: 'center', display: 'inline-flex', gap: '8px' }}>
      <RafShareIcon />
      <span>{children}</span>
    </span>
  )
}

function RafButton({ children, onClick }: RafButtonProps) {
  return (
    <>
      <Box display={[Display.Block, Display.None, Display.None, Display.None]} width="100%">
        <Button
          colorVariant={ButtonColorVariant.Primary}
          data-action-type="raf"
          onClick={onClick}
          size={ButtonSize.Medium}
          type="button"
          width="100%"
        >
          <RafButtonContent>{children}</RafButtonContent>
        </Button>
      </Box>
      <Box display={[Display.None, Display.Block, Display.Block, Display.Block]} width="100%">
        <Button
          colorVariant={ButtonColorVariant.Secondary}
          data-action-type="raf"
          onClick={onClick}
          size={ButtonSize.Medium}
          type="button"
          width="100%"
        >
          <RafButtonContent>{children}</RafButtonContent>
        </Button>
      </Box>
    </>
  )
}

export function RafCampaignAction({ campaign, onActionClick }: RafCampaignActionProps) {
  const handleClick = () => {
    onActionClick?.('raf')
  }

  return (
    <RafActionCard imageTestId="raf-campaign-image" testId="raf-campaign-action">
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
        <Heading2 color={Color.ColdGrey_800} size={[3, 5]}>
          {campaign.title}
        </Heading2>
        <Body2 color={Color.ColdGrey_800}>{campaign.subtitle}</Body2>
      </Box>

      <RafCampaignTiers tiers={campaign.tiers} />

      <RafButton onClick={handleClick}>{campaign.send_cta}</RafButton>
    </RafActionCard>
  )
}

export function RafStatusAction({ status, onActionClick }: RafStatusActionProps) {
  const handleClick = () => {
    onActionClick?.('raf')
  }

  return (
    <RafActionCard imageTestId="raf-status-image" testId="raf-status-action">
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
        <Heading2 color={Color.ColdGrey_800} size={[3, 5]}>
          {status.title}
        </Heading2>
        <Body2 color={Color.ColdGrey_800}>{status.subtitle}</Body2>
      </Box>

      <RafStatusMetrics status={status} />

      <RafButton onClick={handleClick}>{status.invite_cta}</RafButton>
    </RafActionCard>
  )
}
